/**
 * Общий материал игровых блоков статистики (CS2, Dota, Fortnite, League, Destiny).
 *
 * Раньше каждая карточка тонировала затемнение в цвет своей игры — красноватое у Dota,
 * фиолетовое у Fortnite, синее у League. В ленте, где посты идут подряд, пять разных
 * тонировок и пять рамок ярче границ платформы читались как пять разных интерфейсов.
 *
 * Теперь наоборот: шторка, рамка, панель и подписи — одни и те же у всех, в нейтральном
 * сером тоне платформы (brand — hsl(228, 11.6%)). Игру различают две вещи: фоновая
 * картинка, проступающая справа сквозь шторку, и акцентный цвет внутри — числа, кольца,
 * чипы. Цвет остаётся языком игры, материал плашки — языком платформы.
 */

/**
 * Слева перекрытие почти полное — там лежат цифры и подписи, и им нужен ровный тёмный фон.
 * Справа кадр проступает на 16%: фоны теперь не гравировка, а кинокадр места, где играют,
 * и на 10% от него в ленте не оставалось ничего, кроме лёгкого пятна.
 *
 * Так ярко его можно держать только потому, что числа стоят не на голом кадре: под каждой
 * сеткой чисел лежит STATS_TILES, который гасит картинку именно там, где цифры. Поднимать
 * дальше 20% нельзя — кадр начинает спорить с подписями-капслоками, у них альфа .6.
 *
 * Числа в градиенте — доля кадра, который остаётся виден: 1.5% слева, 4.5% на 44% ширины,
 * 16% у правого края. Картинка и шторка сведены в один background, чтобы фон плашки
 * оставался одной строкой.
 */
export const STATS_SCRIM =
  'linear-gradient(100deg, hsla(228,12%,9.2%,.985) 0%, hsla(228,12%,10.4%,.955) 44%, hsla(228,12%,12.7%,.84) 100%)';

/**
 * Собирает фон карточки: шторка → картинка игры → запасной градиент.
 * Картинка декоративная: не загрузилась — останется градиент, ничего не сломается.
 *
 * @param bgImage путь к фону игры в public/game-bg
 * @param fallback градиент в цвете игры на случай, если картинки нет
 */
export const statsSurface = (bgImage: string, fallback: string) =>
  `${STATS_SCRIM}, url(${bgImage}) center / cover no-repeat, ${fallback}`;

/**
 * Фон плашки статистики на продуктовых страницах — обложка игры, обесцвеченная до тона платформы.
 *
 * Один источник на все игры. Нарисованные кадры в `public/game-bg` получили семь игр из двадцати,
 * и продукт делился на игры «с картинкой» и «без»; сама картинка была ни на что в игре не похожа.
 * Обложка есть у каждой игры, всегда актуальна и уже лежит в кэше — ей же нарисована шапка заявки.
 * (Лендинг остаётся на своих кадрах: там нет каталога, только слаг игры.)
 *
 * Над обложкой два слоя:
 *
 * 1. Обесцвечивание через `background-blend-mode: saturation`. Серая заливка отдаёт нижнему слою
 *    свою насыщенность, то есть ноль, и от кадра остаётся только светлота. Так плашка держит
 *    общий серый тон платформы, а не красится в цвет игры: на цветной обложке из-под шторки лез
 *    оранжевый налив у CS2 прямо под числом уровня и синева у Deadlock.
 *    Делается в CSS, а не в imgproxy: imgproxy настроен только на проде, и иначе в деве фон был
 *    бы цветным, а на проде серым.
 * 2. Шторка плотнее прежней — .93 у правого края против .84. Кадр должен угадываться, а не
 *    рассматриваться: это подложка под числа.
 */
const STATS_SCRIM_COVER =
  'linear-gradient(100deg, hsla(228,12%,9.2%,.995) 0%, hsla(228,12%,10.4%,.975) 44%, hsla(228,12%,12.7%,.93) 100%)';

/** Слой обесцвечивания. Тон платформы, чтобы результат не уходил в синеву. */
const DESATURATE_LAYER = 'linear-gradient(hsl(228,12%,50%), hsl(228,12%,50%))';

const coverLayers = (image: string, fallback: string) => ({
  background: `${STATS_SCRIM_COVER}, ${DESATURATE_LAYER}, ${image} center / cover no-repeat, ${fallback}`,
  // Порядок слоёв: шторка обычным режимом, серая заливка режимом насыщенности (она и обесцвечивает
  // всё, что ниже), картинка и запасная заливка обычным.
  backgroundBlendMode: 'normal, saturation, normal, normal',
});

/**
 * Фон по ссылке на обложку. Возвращает объект, а не строку: `background-blend-mode` — отдельное
 * свойство, и отдавать его порознь значило бы дать забыть половину. Раскрывается спредом.
 */
export const coverSurface = (coverUrl: string, fallback: string) =>
  coverLayers(`url(${coverUrl})`, fallback);

/**
 * То же, но картинку кладёт `GameStatsSection` в переменную `--stats-bg-image`: блоки статистики
 * собирают свой фон на уровне модуля, когда игры ещё не знают.
 */
export const coverSurfaceVar = (fallback: string) =>
  coverLayers('var(--stats-bg-image, none)', fallback);

/**
 * Скругление плашки — то же, что у карточки поста, в которой она лежит (radii.xl, 12px).
 * На 8px широкая плашка на странице поста читалась как «обрезанный угол из другой системы»,
 * а у Fortnite углы и вовсе были срезаны фаской.
 */
export const STATS_RADIUS = 'xl';

/**
 * Скругление плашки в ленте, где она лежит ВНУТРИ карточки поста (radii.lg, 8px).
 * На странице поста плашка — сама поверхность и держит 12px; в ленте те же 12px делали
 * её ровно такой же круглой, как контейнер, и она переставала читаться как вложенная.
 */
export const STATS_RADIUS_CARD = 'lg';

/** Скругление вложенного: панели, подносы, чипы. На ступень меньше плашки (radii.lg, 8px). */
export const STATS_RADIUS_INNER = 'lg';

/** Подложка внутренних панелей — нейтральная, чтобы не сдвигать тон картинки под ней. */
export const STATS_PANEL = 'rgba(9, 10, 13, 0.5)';

/**
 * Контур числовых блоков — панелей и подносов.
 *
 * Раньше блоки держались только заливкой, а заливка тут тёмная поверх тёмного: слева, где
 * шторка почти непрозрачная, панель и плашка сливались в одно поле, а справа, где проступает
 * кадр, та же панель вдруг обретала край. Получалось, что структура карточки видна в правой
 * половине и пропадает в левой — блоки читались по-разному в разных местах одной плашки.
 *
 * Линия решает это тем, что не зависит от того, что под ней: белая с малой альфой одинаково
 * видна и на чёрном, и на кадре. .07 — порог, на котором край читается на самом тёмном
 * участке (≈+16 уровней яркости) и ещё не превращается в рамку; на .10 карточка начинала
 * выглядеть расчерченной на клетки.
 *
 * Линия одна на все контейнеры с числами — панели, подносы, плитки режимов Fortnite. Чипы
 * (оружие, герои, уровень) её не получают: это подписи, а не блоки, и обведённые чипы дают
 * ту самую пестроту, ради борьбы с которой блоки свели на общий материал.
 */
export const STATS_HAIRLINE = 'rgba(255, 255, 255, 0.07)';

/**
 * Панель с числами: подложка + контур. Спред вместо голого `background: STATS_PANEL`,
 * чтобы линия приезжала вместе с заливкой и панель нельзя было завести без края.
 */
export const STATS_PANEL_SURFACE = {
  bgColor: STATS_PANEL,
  boxShadow: `inset 0 0 0 1px ${STATS_HAIRLINE}`,
} as const;

/**
 * Рамка — та же граница, что у остальных элементов платформы (whiteAlpha.200), без цвета игры.
 * Цветная рамка была самой заметной частью карточки и первой выдавала «пять разных дизайнов».
 */
export const STATS_EDGE = 'rgba(255, 255, 255, 0.08)';

/** Подписи-капслоки во всех пяти блоках. Раньше альфы разъехались от .58 до .62. */
export const STATS_LABEL = 'rgba(214, 217, 226, 0.6)';

/**
 * Материал ячейки с числом — раскрытая полка в ленте и сетки в полном блоке.
 *
 * Числам нужна опора: без подложки они висели прямо на кадре, и у «47%» справа фон проступал
 * сквозь цифру, отнимая у неё край. Подложка заодно гасит картинку ровно там, где стоят цифры,
 * а не по всей плашке — поэтому шторку справа можно держать прозрачной.
 *
 * Раньше подложка была одна на всю сетку — общий поднос. Пять чисел под одним контуром читались
 * как одно поле: глаз искал границу между показателями и не находил. Теперь материал переехал
 * на саму ячейку, и каждое число — отдельный факт в своей коробке.
 *
 * Почему плитки, а не делители внутри подноса: сетка переносится (auto-fit), и в неполной
 * последней строке на месте недостающей ячейки оставалась пустая клетка. Делители выигрывают
 * только на широкой карточке, а в ленте карточка узкая почти всегда.
 *
 * Заливка тише панели (.38 против .5) — панель это объект, плитка это число; контур общий,
 * STATS_HAIRLINE, иначе они различались бы не «весом», а самим фактом наличия края.
 */
const STATS_TILE_BG = 'rgba(9, 10, 13, 0.38)';

/**
 * Сетка числовых плиток: спред кладётся на КОНТЕЙНЕР, а материал раздаётся детям селектором.
 * Так все десять сеток в пяти блоках получают плитки без правки самих ячеек — ячейки в каждом
 * блоке свои (StatCell, MiniStat, ячейки полки), и трогать их поодиночке значило бы разъехаться.
 *
 * Зазор 2 (8px) — меньше прежнего 3: между плитками с краями воздуха нужно меньше, чем между
 * числами, у которых кроме воздуха ничего не было.
 */
export const STATS_TILES = {
  gap: 2,
  css: {
    '& > *': {
      minWidth: 0,
      borderRadius: '8px', // radii.lg — то же скругление вложенного, что у панелей и чипов
      padding: '0.6rem 0.7rem',
      backgroundColor: STATS_TILE_BG,
      boxShadow: `inset 0 0 0 1px ${STATS_HAIRLINE}`,
    },
  },
} as const;
